import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {debounceTime, filter, take} from 'rxjs/operators';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {EditFormService} from './edit-form.service';
import {Store} from '@ngrx/store';
import {NamenspruefungErgebnisDTO, ZahlungDTO, ZahlungUpdateRequestDTO} from 'src/app/openapi/zahlung-openapi';
import {WizardActionButton} from '../../templates/wizard/wizard.component';
import {IntentActionService} from '../../services/intent.action.service';
import {AppState} from '../../interfaces/app-state.interface';
import {AppContextSelectors} from '../../store/selectors/app-context.selectors';
import {AppContextActions} from '../../store/actions/app-context.actions';
import {TransactionActions} from '../../store/actions/transaction.actions';
import {ZahlungSelectors} from '../../store/selectors/zahlung.selectors';
import {ZahlungActions} from '../../store/actions/zahlung.actions';
import {BelegActions} from '../../store/actions/beleg.actions';
import {ZahlungsprotokollActions} from '../../store/actions/zahlungsprotokoll.actions';
import {FormActionButtons, InhaberEntitiesSelectors} from '@adnova/jf-ng-components';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';


@Component({
  selector: 'zlng-edit-form',
  templateUrl: './edit-form.component.html',
  styleUrls: ['./edit-form.component.scss']
})
export class EditFormComponent implements OnInit {

  private _store = inject(Store<AppState>);
  private _destroyRef = inject(DestroyRef);
  private _editFormService = inject(EditFormService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _intentActionService = inject(IntentActionService);

  private _zahlungId = new BehaviorSubject('');
  private _inhaberId = new BehaviorSubject('');
  private _formActionsButtons: FormActionButtons = {
    primaryButton: {
      label: 'Überweisen',
      icon: 'arrowRight',
      iconPosition: 'left',
      tooltipPosition: {vertical: 'top', horizontal: 'center'},
      action: () => {
        this.saveZahlung(true);
      },
    },
    secondaryButtons: [
      {
        label: 'Speichern und später überweisen',
        action: () => {
          this.saveZahlung(false);
        },
      },
    ],
  };

  private _angewiesen = false;
  private _zahlungsprotokollId?: string;
  private _isNotEditable = false;
  private _isBelegCancelled = false;
  private _isFormValid = false;
  private _isFormChanged = false;
  private _wizardActionButtons = new BehaviorSubject<WizardActionButton[]>([]);
  private _popupMode = false;

  get formActionsButtons(): FormActionButtons {
    return this._formActionsButtons;
  }

  get angewiesen(): boolean {
    return this._angewiesen;
  }

  get zahlungsprotokollId(): string | undefined {
    return this._zahlungsprotokollId;
  }

  get isNotEditable(): boolean {
    return this._isNotEditable;
  }

  get isBelegCancelled(): boolean {
    return this._isBelegCancelled;
  }

  constructor() {
  }

  ngOnInit(): void {
    /*
     * INFO: Sofern die Bearbeiten-Seite aufgerufen wird, müssen die
     * - StartTransaction
     * - closeEditPage
     * - saveActionSuccessed
     * Kennzeichen zurückgesetzt werden.
     * Ist dies nicht der fall, so würde nach dem erstmaligen Ausführen einer Zahlung hier immer direkt auf die
     * Bankauswahl-Seite oder auf die Übersicht navigiert werden.
     */
    this._store.dispatch(ZahlungActions.resetEditPageOnInit());
    this._store.dispatch(TransactionActions.setFinalZahlungDtos({zahlungDtos: []}));

    // INFO: Action Buttons für die Navigation an den Header senden
    this._wizardActionButtons.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(0),
    ).subscribe(buttons => {
      this._store.dispatch(AppContextActions.setWizardActionButtons({
        wizardActionButtons: buttons,
      }));
    });

    this._store.select(ZahlungSelectors.filterStatusOpen).pipe(
      take(1),
    ).subscribe(filterStatusOpen => {
      const status = filterStatusOpen ? 'open' : 'all';
      this._wizardActionButtons.next([
        {
          title: 'Zur Übersicht',
          href: `/overview/inhaber/`,
          icon: 'arrowLeft',
          showInPopup: false,
          queryParams: {status},
        },
      ]);
    });

    this._route.paramMap.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(
      paramMap => {
        this.zahlungId = paramMap.get('zahlungId') || '';
      }
    );

    this._store.select(InhaberEntitiesSelectors.currentInhaber).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(inhaber => {
      if (!inhaber) return;

      this.inhaberId = inhaber.id;

      this._wizardActionButtons.next(this._wizardActionButtons.value.map(button => {
        const newButton = {...button};
        if (newButton.title === 'Zur Übersicht') {
          newButton.href += this.inhaberId;
        }
        return newButton;
      }));
    });

    combineLatest([
      this._inhaberId,
      this._zahlungId,
    ]).pipe(
      filter(([inhaberId, zahlungId]) => !!inhaberId && !!zahlungId),
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(([inhaberId, zahlungId]) => {
      this._store.dispatch(
        ZahlungActions.getZahlung({inhaberId, zahlungId})
      );
    });

    combineLatest([
      this._inhaberId,
      this._store.select(ZahlungSelectors.belegId),
    ]).pipe(
      filter(([inhaberId, belegId]) => !!inhaberId && !!belegId && !this._angewiesen),
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(([inhaberId, belegId]) => {
      this._store.dispatch(BelegActions.getBelegSuggestions({
        inhaberId,
        belegId: belegId!,
      }));
    });

    this._editFormService.beleg.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(beleg => {
      if (beleg) {
        const empfaengerDto = {
          id: beleg.partner?.id,
          bezeichnung: beleg.partner?.bezeichnung,
        };

        this._store.dispatch(ZahlungActions.changedFormValues({
          empfaengerDto,
          betrag: beleg.betrag,
        }));
      }
    });

    this._store.select(ZahlungSelectors.iban).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(iban => {
      if (iban.length <= 8) return;

      this._store.dispatch(
        ZahlungActions.readBank({
          iban,
        })
      );
    });

    combineLatest([
      this._store.select(ZahlungSelectors.zahlungDto),
      this._store.select(ZahlungSelectors.startTransaction),
      this._store.select(ZahlungSelectors.saveActionSuccessedOrNotNeeded),
    ]).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(([
                   zahlungDto,
                   shouldStartTransaction,
                   saveSuccessed,
                 ]) => {

      // INFO: ZahlungId aktualisieren, da eine Zahlung neu erstellt oder verändert worden sein kann
      if (!this.zahlungId && zahlungDto) {
        this.zahlungId = zahlungDto.id;
      }

      this._zahlungsprotokollId = zahlungDto?.zahlungsprotokollId;

      if (shouldStartTransaction && saveSuccessed && zahlungDto) {
        this.doTransfer();
      }
    });

    this._store.select(AppContextSelectors.popupMode).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(popupMode => {
      this._popupMode = popupMode;
    });

    combineLatest([
      this._store.select(ZahlungSelectors.closeEditPage),
      this._store.select(ZahlungSelectors.saveActionSuccessedOrNotNeeded),
    ]).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(([close, saveSuccessed]) => {
      if (close && saveSuccessed) {
        if (this._popupMode && this._intentActionService.isRunningInPortal()) {
          this._intentActionService.doCloseDialogV1();
        } else {
          this._router.navigateByUrl('/overview/inhaber/' + this.inhaberId);
        }
      }
    });

    this._store.select(ZahlungSelectors.angewiesen).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(angewiesen => {
      this._angewiesen = angewiesen || false;
      this.handlePrimaryButtonState();
    });

    this._store.select(ZahlungSelectors.notEditable).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(isNotEditable => {
      this._isNotEditable = isNotEditable || false;
      this.handleSecondaryButtonState();
    });

    this._store.select(ZahlungSelectors.assignedBelegStatusCancelled).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(cancelled => {
      this._isBelegCancelled = cancelled || false;
      this.handlePrimaryButtonState();
    });

    this._store.select(ZahlungSelectors.isFormValid).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(isFormValid => {
      this._isFormValid = isFormValid;
      this.handlePrimaryButtonState();
      this.handleSecondaryButtonState();
    });

    this._store.select(ZahlungSelectors.isFormChanged).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(isFormChanged => {
      this._isFormChanged = isFormChanged;
      this.handlePrimaryButtonState();
      this.handleSecondaryButtonState();
    });
  }

  handlePrimaryButtonState(): void {
    this._formActionsButtons.primaryButton!.disabled = this._isBelegCancelled || this._angewiesen || !this._isFormValid || !this._isFormChanged;
    this._formActionsButtons.primaryButton!.tooltip = this._isBelegCancelled ? 'Zahlungen mit storniertem Beleg können nicht überwiesen werden.' : '';
  }

  handleSecondaryButtonState(): void {
    this._formActionsButtons.secondaryButtons![0].disabled =
      this._isBelegCancelled
      || this._angewiesen
      || this._isNotEditable
      || !this._isFormValid
      || !this._isFormChanged;
  }

  get inhaberId(): string {
    return this._inhaberId.getValue();
  }

  set inhaberId(inhaberId: string) {
    this._inhaberId.next(inhaberId);
  }

  get zahlungId(): string {
    return this._zahlungId.getValue();
  }

  set zahlungId(zahlungId: string) {
    this._zahlungId.next(zahlungId);
  }

  saveZahlung(shouldStartTransaction: boolean): void {
    if (this.zahlungId) {
      this.updateZahlung(shouldStartTransaction);
    } else {
      this.createZahlung(shouldStartTransaction);
    }
  }

  private createZahlung(shouldStartTransaction: boolean): void {
    if (!this.inhaberId) return;

    this._store.select(ZahlungSelectors.zahlungCreateRequestDTO).pipe(
      take(1),
    ).subscribe(zahlungCreateRequestDto => {
      if (!zahlungCreateRequestDto) return;
      this._store.dispatch(ZahlungActions.createZahlung({
        inhaberId: this.inhaberId,
        requestDto: zahlungCreateRequestDto,
        startTransaction: shouldStartTransaction
      }));
    });
  }

  private updateZahlung(shouldStartTransaction: boolean): void {
    combineLatest([
      this._store.select(ZahlungSelectors.actualZahlungDto),
      this._store.select(ZahlungSelectors.zahlungUpdateRequestDTO),
    ]).pipe(
      take(1),
    ).subscribe(([actualZahlungDto, zahlungUpdateRequestDto]) => {
      if (!actualZahlungDto || !zahlungUpdateRequestDto) return;

      if (this.isZahlungEqual(actualZahlungDto, zahlungUpdateRequestDto) || this._isNotEditable) {
        this._store.dispatch(ZahlungActions.updateZahlungNotNeeded({
          startTransaction: shouldStartTransaction
        }));
      } else {
        this._store.dispatch(ZahlungActions.updateZahlung({
          inhaberId: this.inhaberId,
          zahlungId: this.zahlungId,
          requestDto: zahlungUpdateRequestDto,
          startTransaction: shouldStartTransaction,
        }));
      }
    });
  }

  doTransfer(): void {
    this._store.select(ZahlungSelectors.zahlungDto).pipe(
      take(1),
    ).subscribe(zahlungDto => {
      if (!zahlungDto) return;

      this._store.dispatch(TransactionActions.setFinalZahlungDtos({zahlungDtos: [zahlungDto]}));

      this._router.navigateByUrl('transaction/inhaber/' + this.inhaberId + '/bank');
    });
  }

  /**
   * Vergleicht die aktuellen Daten der Zahlung, mit denen welche ursprünglich von der DB geladen wurden.
   *
   * @param actualzahlung Die ursprünglichen Zahlungsdaten
   * @param requestDto Die neuen Daten der Zahlung
   * @private
   */
  private isZahlungEqual(
    zahlungDto: ZahlungDTO,
    requestDto: ZahlungUpdateRequestDTO
  ): boolean {
    return zahlungDto
      && zahlungDto.empfaenger.bezeichnung === requestDto.empfaenger.bezeichnung
      && zahlungDto?.iban === requestDto.iban
      && zahlungDto.betrag === requestDto.betrag
      && zahlungDto.verwendungszweck === requestDto.verwendungszweck
      && zahlungDto.faelligkeitsdatum === requestDto.faelligkeitsdatum
      && zahlungDto.bic === requestDto.bic
      && zahlungDto.bankbezeichnung === requestDto.bankbezeichnung
      && zahlungDto.belegId === requestDto.belegId;
  }


  /**
   * Öffnet das Zahlungsprotokoll des erfolgreichen Zahlungslaufs dieser Überweisung.
   */
  doOpenZahlungsprotokoll(): void {
    if (!this.inhaberId || !this._zahlungsprotokollId) return;

    this._store.dispatch(ZahlungsprotokollActions.openZahlungsprotokoll({
      inhaberId: this.inhaberId,
      zahlungslaufId: this._zahlungsprotokollId,
    }));
  }
}
